import React, { useEffect, useState as useReactState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Menu,
  Tooltip,
  Box,
  Typography,
  Slider,
  Stack,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GrDocumentVideo } from "react-icons/gr";
import { MdOutlineFileDownload, MdOutlineFeedback } from "react-icons/md";
import { RiEditLine, RiShareForwardLine } from "react-icons/ri";
import { FaChevronDown } from "react-icons/fa";
import { 
  PlayArrow as PlayArrowIcon, 
  Pause as PauseIcon, 
  VolumeUp as VolumeUpIcon, 
  VolumeOff as VolumeOffIcon,
  ClosedCaption as ClosedCaptionIcon, 
  ClosedCaptionDisabled as ClosedCaptionDisabledIcon 
} from "@mui/icons-material";
import PropTypes from "prop-types";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AbstractVideoViewDialog = ({
  open,
  onClose,
  videoSrc,
  videoProjectId,
  initialVersion,
  allVersions,    // cross-project versions from PreviousAssetsModal [{version_number, display_version, project_id, created_at}, ...]
  onStartRegenerate, 
  onDownload,
  onEdit,
  professionalType,
}) => {
  const [selectedVersion, setSelectedVersion] = React.useState(1);
  const [modalProfessionalType, setModalProfessionalType] = React.useState(professionalType);
  const [totalVersions, setTotalVersions] = React.useState(1);
  const [alertInfo, setAlertInfo] = React.useState(null);
  const [loadingVideo, setLoadingVideo] = useReactState(false);
  const [loadingVersionInfo, setLoadingVersionInfo] = useReactState(true);
  const [fetchedVideoUrl, setFetchedVideoUrl] = useReactState(null);

  // Cross-project version map: displayVersion -> { project_id, version_number }
  const crossVersionMap = React.useMemo(() => {
    if (allVersions && allVersions.length > 0) {
      const vMap = {};
      allVersions.forEach((v, index) => {
        const dv = Number(v.display_version || (index + 1));
        vMap[dv] = { project_id: v.project_id, version_number: v.version_number, professional_type: v.professional_type };
      });
      return vMap;
    }
    return null;
  }, [allVersions]);

  const activeProjectId = crossVersionMap && crossVersionMap[selectedVersion] 
    ? crossVersionMap[selectedVersion].project_id 
    : videoProjectId;
  const [downloadProgress, setDownloadProgress] = useReactState(0);
  const [shareDialogOpen, setShareDialogOpen] = useReactState(false);
  const [shareUrl, setShareUrl] = useReactState("");
  const [sharingInProgress, setSharingInProgress] = useReactState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useReactState(false);
  const [feedbackRating, setFeedbackRating] = useReactState(0);
  const [feedbackComments, setFeedbackComments] = useReactState("");
  const [feedbackConsent, setFeedbackConsent] = useReactState(false);
  const [submittingFeedback, setSubmittingFeedback] = useReactState(false);
  const [previousFeedbacks, setPreviousFeedbacks] = useReactState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useReactState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useReactState(null);
  const [mlrStatus, setMlrStatus] = useReactState(false);
  const [showCaptions, setShowCaptions] = useReactState(false);
  const [captionsUrl, setCaptionsUrl] = useReactState(null);
  const [loadingCaptions, setLoadingCaptions] = useReactState(false);
  const [activeCaptionText, setActiveCaptionText] = useReactState("");
  const [isPlaying, setIsPlaying] = useReactState(false);
  const [currentTime, setCurrentTime] = useReactState(0);
  const [duration, setDuration] = useReactState(0);
  const [volume, setVolume] = useReactState(1);
  const [isMuted, setIsMuted] = useReactState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useReactState(false);

  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const PRIMARY_BLUE = "#1E4DE3";

  // activeProjectId is now derived from props and selectedVersion

  useEffect(() => {
    setModalProfessionalType(professionalType);
  }, [professionalType]);

  const displayVideoSrc = videoSrc || fetchedVideoUrl;

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle fullscreen toggle
  const handleFullscreen = () => {
    if (!videoContainerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error("Error exiting fullscreen:", err);
      });
    } else {
      videoContainerRef.current.requestFullscreen().catch((err) => {
        console.error("Error entering fullscreen:", err);
      });
    }
  };
  
  // Get organization data to check if MLR downloads should be disabled
  const organizationData = JSON.parse(localStorage.getItem("organizationData") || "{}");
  const disableMlrDownload = organizationData?.id === 4;

  // Fetch MLR status and poll every 5 seconds while dialog is open (stop when MLR is ready)
  useEffect(() => {
    if (!open || !activeProjectId || loadingVersionInfo) {
      return;
    }
    const token = localStorage.getItem("authToken");
    const fetchMlrStatus = async () => {
      try {
        const response = await fetch(`${BASE_URL}/video-mlr-status?videoProjectId=${activeProjectId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const isReady = data.video_mlr === true || data.video_mlr === "True";
          setMlrStatus(isReady);
          // Stop polling if MLR is ready
          if (isReady && intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (error) {
        console.error("Error fetching MLR status:", error);
      }
    };
    
    // Only start polling if MLR is not already ready
    let intervalId;
    fetchMlrStatus();
    if (!mlrStatus) {
      intervalId = setInterval(fetchMlrStatus, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [open, activeProjectId, mlrStatus, loadingVersionInfo]);

  // Fetch version info when dialog opens
  useEffect(() => {
    let isMounted = true;

    // If cross-project allVersions are provided, use them directly
    if (open && allVersions && allVersions.length > 0) {
      if (isMounted) {
        setTotalVersions(allVersions.length);
        
        const latestVersionObj = allVersions[allVersions.length - 1];
        const latestDv = Number(latestVersionObj.display_version || allVersions.length);
        
        setSelectedVersion(initialVersion ? Number(initialVersion) : latestDv);
        setLoadingVersionInfo(false);
      }
      return () => { isMounted = false; };
    }

    // Otherwise, fall back to fetching from the single-project endpoint
    const fetchVersionInfo = async () => {
      console.log("[AbstractVideoViewDialog] fetchVersionInfo check:", { open, videoProjectId });
      if (!open || !videoProjectId) return;
      
      setLoadingVersionInfo(true);
      const token = localStorage.getItem("authToken");
      
      try {
        const response = await fetch(
          `${BASE_URL}/video_project_current_version?videoProjectId=${videoProjectId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.ok && isMounted) {
          const data = await response.json();
          const currentVersion = data.video_current_version?.version_number || 1;
          const total = data.video_current_version?.total_versions || 1;
          
          setSelectedVersion(initialVersion || currentVersion);
          setTotalVersions(total);
        }
      } catch (error) {
        console.error("[AbstractVideoViewDialog] Error fetching version info:", error);
      } finally {
        if (isMounted) setLoadingVersionInfo(false);
      }
    };

    fetchVersionInfo();
    return () => { isMounted = false; };
  }, [open, videoProjectId, initialVersion, allVersions]);

  // Fetch video as blob for authenticated streaming endpoint
  useEffect(() => {
    let currentXhr = null;

    const fetchVideoBlob = async () => {
      console.log("[AbstractVideoViewDialog] fetchVideoBlob check:", { open, activeProjectId, loadingVersionInfo, videoSrc, selectedVersion, crossVersionMap });
      if (!open || !activeProjectId || loadingVersionInfo) {
        console.log("[AbstractVideoViewDialog] fetchVideoBlob skipped", {
          open,
          activeProjectId,
          loadingVersionInfo,
          videoSrc,
          selectedVersion,
        });
        return;
      }
      // If we already have a videoSrc prop, use that instead
      if (videoSrc) {
        console.log("[AbstractVideoViewDialog] Using existing videoSrc; skipping fetch");
        setFetchedVideoUrl(null);
        return;
      }

      // Determine which version number to pass to the API
      let apiVersionNumber = selectedVersion;
      if (crossVersionMap && crossVersionMap[selectedVersion]) {
        apiVersionNumber = crossVersionMap[selectedVersion].version_number;
      }
      
      setLoadingVideo(true);
      setDownloadProgress(0);
      const token = localStorage.getItem("authToken");
      
      try {
        // Use XMLHttpRequest for progress tracking
        const xhr = new XMLHttpRequest();
        currentXhr = xhr;
        const cacheBuster = new Date().getTime();
        xhr.open('GET', `${BASE_URL}/video/${activeProjectId}?versionNumber=${apiVersionNumber}&_cb=${cacheBuster}`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'blob';
        
        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setDownloadProgress(percentComplete);
          }
        };
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            const blob = xhr.response;
            const blobUrl = URL.createObjectURL(blob);
            setFetchedVideoUrl(prev => {
              if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
              return blobUrl;
            });
          } else {
            console.error("[AbstractVideoViewDialog] Failed to load video:", xhr.status);
            setAlertInfo({ severity: "error", message: "Failed to load video" });
          }
          setLoadingVideo(false);
        };
        
        xhr.onerror = () => {
          console.error("[AbstractVideoViewDialog] Error fetching video");
          setAlertInfo({ severity: "error", message: "Failed to load video" });
          setLoadingVideo(false);
        };
        
        xhr.send();
      } catch (error) {
        console.error("[AbstractVideoViewDialog] Error fetching video:", error);
        setAlertInfo({ severity: "error", message: "Failed to load video" });
        setLoadingVideo(false);
      }
    };

    fetchVideoBlob();
    return () => {
      if (currentXhr) {
        currentXhr.abort();
      }
      // We no longer revoke the blob URL here to prevent the video from blanking out
      // during the dialog's exit animation. It is handled by a dedicated cleanup effect.
    };
  }, [open, activeProjectId, videoSrc, selectedVersion, crossVersionMap, loadingVersionInfo]);

  // Fetch subtitles (VTT) as blob
  useEffect(() => {
    const fetchSubtitles = async () => {
      if (!open || !activeProjectId || !showCaptions || loadingVersionInfo) {
        return;
      }
      
      setLoadingCaptions(true);
      const token = localStorage.getItem("authToken");
      
      let apiVersionNumber = selectedVersion;
      if (crossVersionMap && crossVersionMap[selectedVersion]) {
        apiVersionNumber = crossVersionMap[selectedVersion].version_number;
      }

      try {
        const response = await fetch(`${BASE_URL}/video/${activeProjectId}/captions?versionNumber=${apiVersionNumber}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setCaptionsUrl(prev => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return url;
          });
        }
      } catch (error) {
        console.error("[AbstractVideoViewDialog] Error fetching subtitles:", error);
      } finally {
        setLoadingCaptions(false);
      }
    };

    fetchSubtitles();
  }, [open, activeProjectId, showCaptions, selectedVersion, crossVersionMap, loadingVersionInfo]);

  // Handle caption cue changes for custom overlay
  const handleCueChange = (event) => {
    const track = event.target.track;
    if (track) {
      // Hide native captions but keep track active for cues
      track.mode = "hidden";
      const activeCues = track.activeCues;
      if (activeCues && activeCues.length > 0) {
        setActiveCaptionText(activeCues[0].text);
      } else {
        setActiveCaptionText("");
      }
    }
  };

  // Sync volume and mute state with video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // Defer clearing URLs to allow the exit animation to complete smoothly
      const timer = setTimeout(() => {
        setFetchedVideoUrl(prev => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return null;
        });
        setCaptionsUrl(prev => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return null;
        });
      }, 300);

      setShowCaptions(false);
      setActiveCaptionText("");
      setSelectedVersion(1);
      setTotalVersions(1);
      setLoadingVideo(false);
      setDownloadProgress(0);
      setCurrentTime(0);
      setIsPlaying(false);
      setDuration(0);
      setLoadingVersionInfo(true);

      return () => clearTimeout(timer);
    }
  }, [open, setFetchedVideoUrl, setCaptionsUrl, setShowCaptions, setActiveCaptionText, setSelectedVersion, setTotalVersions, setLoadingVideo, setDownloadProgress, setCurrentTime, setIsPlaying, setDuration]);



  const handleChangeVersion = (event) => {
    const newVersion = event.target.value;
    setSelectedVersion(newVersion);
    // Reset playback state when switching versions
    setCurrentTime(0);
    setIsPlaying(false);
    setDuration(0);
    
    // If cross-project versions, update professionalType when switching
    if (crossVersionMap && crossVersionMap[newVersion]) {
      if (crossVersionMap[newVersion].professional_type) {
        setModalProfessionalType(crossVersionMap[newVersion].professional_type);
      }
    }

    // Revoke old blob URL before fetching new version
    if (fetchedVideoUrl && fetchedVideoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fetchedVideoUrl);
    }
    setFetchedVideoUrl(null);
  };

  // Generate version options based on totalVersions
  const versionOptions = crossVersionMap
    ? Object.keys(crossVersionMap).map(Number).sort((a,b) => a - b)
    : Array.from({ length: totalVersions }, (_, i) => i + 1);

 
  const handleComingSoon = () => {
    setAlertInfo({ severity: "info", message: "Coming soon" });
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

 
  const handleDownload = async (downloadVideo = true, downloadVideoMlr = false) => {
    handleDownloadClose();
    if (!activeProjectId || !selectedVersion) {
      setAlertInfo({ severity: "error", message: "Video not available for download" });
      return;
    }

    const token = localStorage.getItem("authToken");
    
    // Determine which version number to pass to the API
    let apiVersionNumber = selectedVersion;
    if (crossVersionMap && crossVersionMap[selectedVersion]) {
      apiVersionNumber = crossVersionMap[selectedVersion].version_number;
    }

    try {
      setAlertInfo({ severity: "info", message: "Starting download..." });
      
      let downloadUrl;
      let fileName;
      
      if (downloadVideoMlr) {
        // Download MLR report
        downloadUrl = `${BASE_URL}/export-video-mlr/${activeProjectId}`;
        fileName = `Pubvision_video_MLR_v${selectedVersion}.zip`;
      } else {
        // Download regular video
        downloadUrl = `${BASE_URL}/video/download/${activeProjectId}?version_id=${apiVersionNumber}`;
        fileName = `Pubvision_video_v${selectedVersion}.mp4`;
      }

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setAlertInfo({ severity: "success", message: "Download started successfully!" });
      } else {
        console.error("[AbstractVideoViewDialog] Download failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to download" });
      }
    } catch (error) {
      console.error("[AbstractVideoViewDialog] Error downloading:", error);
      setAlertInfo({ severity: "error", message: "Error downloading" });
    }

    if (onDownload) {
      onDownload();
    }
  };

 
  const handleCloseAlert = () => {
    setAlertInfo(null);
  };

  const handleShare = async () => {
    if (!activeProjectId) {
      setAlertInfo({ severity: "error", message: "Video not available for sharing" });
      return;
    }

    const token = localStorage.getItem("authToken");
    setSharingInProgress(true);
    setShareDialogOpen(true);
    setShareUrl("");

    try {
      // First, try GET to check if share link already exists
      const getResponse = await fetch(
        `${BASE_URL}/video/${activeProjectId}/share`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.share_url) {
          setShareUrl(getData.share_url);
          setSharingInProgress(false);
          return;
        }
      }

      // If no existing link, create one with POST
      const postResponse = await fetch(
        `${BASE_URL}/video/${activeProjectId}/share`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ videoId: activeProjectId }),
        }
      );

      if (postResponse.ok) {
        const postData = await postResponse.json();
        setShareUrl(postData.share_url);
      } else {
        console.error("[AbstractVideoViewDialog] Share failed:", postResponse.status);
        setAlertInfo({ severity: "error", message: "Failed to create share link" });
        setShareDialogOpen(false);
      }
    } catch (error) {
      console.error("[AbstractVideoViewDialog] Error creating share link:", error);
      setAlertInfo({ severity: "error", message: "Error creating share link" });
      setShareDialogOpen(false);
    } finally {
      setSharingInProgress(false);
    }
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setAlertInfo({ severity: "success", message: "Link copied to clipboard!" });
    } catch (error) {
      console.error("[AbstractVideoViewDialog] Error copying to clipboard:", error);
      setAlertInfo({ severity: "error", message: "Failed to copy link" });
    }
  };

  // Fetch previous feedbacks without opening dialog
  const fetchPreviousFeedbacks = async () => {
    setLoadingFeedbacks(true);
    const token = localStorage.getItem("authToken");

    // Determine which version number to pass to the API
    let apiVersionNumber = selectedVersion;
    if (crossVersionMap && crossVersionMap[selectedVersion]) {
      apiVersionNumber = crossVersionMap[selectedVersion].version_number;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/project_feedbacks?id=${activeProjectId}&type=video&version=${apiVersionNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPreviousFeedbacks(data.feedbacks || []);
      } else {
        console.error("[AbstractVideoViewDialog] Failed to fetch feedbacks:", response.status);
        setPreviousFeedbacks([]);
      }
    } catch (error) {
      console.error("[AbstractVideoViewDialog] Error fetching feedbacks:", error);
      setPreviousFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleOpenFeedback = () => {
    setFeedbackRating(0);
    setFeedbackComments("");
    setFeedbackConsent(false);
    setFeedbackDialogOpen(true);
    // Fetch previous feedbacks
    fetchPreviousFeedbacks();
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      setAlertInfo({ severity: "warning", message: "Please select a rating" });
      return;
    }

    setSubmittingFeedback(true);
    const token = localStorage.getItem("authToken");

    // Determine which version number to pass to the API
    let apiVersionNumber = selectedVersion;
    if (crossVersionMap && crossVersionMap[selectedVersion]) {
      apiVersionNumber = crossVersionMap[selectedVersion].version_number;
    }

    try {
      const formData = new FormData();
      formData.append("stars", feedbackRating);
      formData.append("comments", feedbackComments);
      formData.append("id", activeProjectId);
      formData.append("type", "video");
      formData.append("version", apiVersionNumber);

      const response = await fetch(`${BASE_URL}/submit_feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setAlertInfo({ severity: "success", message: "Feedback submitted successfully!" });
        setFeedbackDialogOpen(false);
        // Reset form fields
        setFeedbackRating(0);
        setFeedbackComments("");
        setFeedbackConsent(false);
      } else {
        console.error("[AbstractVideoViewDialog] Feedback submission failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to submit feedback" });
      }
    } catch (error) {
      console.error("[AbstractVideoViewDialog] Error submitting feedback:", error);
      setAlertInfo({ severity: "error", message: "Error submitting feedback" });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ratingOptions = [
    { value: 1, label: "Awful", emoji: "😣" },
    { value: 2, label: "Poor", emoji: "😞" },
    { value: 3, label: "Neutral", emoji: "😐" },
    { value: 4, label: "Good", emoji: "🤩" },
    { value: 5, label: "Excellent", emoji: "😍" },
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      className="abstract-video-dialog"
      // PaperProps={{
      //   style: {
      //     width: "900px",
      //     maxWidth: "900px",
      //     borderRadius: "8px",
      //     overflow: "visible",
      //   },
      // }}
      slotProps={{
        paper: {
          sx: {
            width: 1000,           
            maxWidth: 1000,
            borderRadius: 2,      
            overflow: "visible",
            fontFamily: "'Jost', sans-serif",
          },
        },
      }}
      sx={{
        "& *": { fontFamily: "'Jost', sans-serif !important" },
      }}
    >

      <IconButton
        style={{ position: "absolute", top: 12, right: 12, color: "#838383", zIndex: 1 }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle style={{ padding: "16px", paddingRight: "64px", borderBottom: "1px solid #eee" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <GrDocumentVideo style={{ color: "#1E4DE3", fontSize: "20px" }} />
             
          </div>
 
          <Select
            size="small"
            value={selectedVersion}
            onChange={handleChangeVersion}
            disabled={loadingVideo || loadingVersionInfo || totalVersions <= 1}
            sx={{
              height: 36,
              fontSize: "14px",
              color: "#1E4DE3",
              minWidth: 80,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d3d3d3",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1E4DE3",
              },
              "&:hover": {
                backgroundColor: "#e8f0fe",
                boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1E4DE3",
              },
            }}
          >
            {versionOptions.map((v) => (
              <MenuItem key={v} value={v}>v{v}</MenuItem>
            ))}
          </Select>
 
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {modalProfessionalType && (
              <Chip
                label={`Audience: ${modalProfessionalType}`}
                variant="outlined"
                sx={{
                  borderColor: "#1E4DE3",
                  color: "#1E4DE3",
                  fontWeight: 600,
                  fontSize: "13px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#e8f0fe",
                  px: 1,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
              />
            )}
 
            <Button
              variant="outlined"
              startIcon={<RiEditLine style={{ color: "#1E4DE3" }} />}
              onClick={() => onEdit(activeProjectId, selectedVersion)}
              sx={{
                borderColor: "#d3d3d3",
                color: "#1E4DE3",
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  borderColor: "#1E4DE3",
                  boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                  backgroundColor: "#e8f0fe",
                },
              }}
            >
              Edit
            </Button>

            <div style={{ display: "flex" }}>
              <Button
                variant="outlined"
                startIcon={<MdOutlineFileDownload style={{ color: "#1E4DE3" }} />}
                onClick={() => handleDownload(true, false)}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: "none",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadClick}
                sx={{
                  minWidth: "32px",
                  padding: "6px 8px",
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                <FaChevronDown style={{ fontSize: "12px" }} />
              </Button>
              <Menu
                anchorEl={downloadAnchorEl}
                open={Boolean(downloadAnchorEl)}
                onClose={handleDownloadClose}
                PaperProps={{
                  style: {
                    fontFamily: "'Inter', sans-serif",
                  },
                }}
                MenuListProps={{
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "14px",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "#e8f0fe",
                        color: "#1e4de3",
                      },
                    },
                  },
                }}
              >
                <MenuItem onClick={() => handleDownload(true, false)}>
                  Download Video
                </MenuItem>
                {!disableMlrDownload && (
                  mlrStatus ? (
                    <MenuItem onClick={() => handleDownload(false, true)}>
                      Download Video MLR
                    </MenuItem>
                  ) : (
                    <Tooltip
                      title="MLR report is in progress, please try after sometime"
                      arrow
                    >
                      <MenuItem style={{ color: "#999", cursor: "not-allowed" }}>
                        Download Video MLR
                      </MenuItem>
                    </Tooltip>
                  )
                )}
              </Menu>
            </div>

   
            <Button
              variant="outlined"
              startIcon={sharingInProgress ? <CircularProgress size={16} sx={{ color: "#1E4DE3" }} /> : <RiShareForwardLine style={{ color: "#1E4DE3" }} />}
              onClick={handleShare}
              disabled={sharingInProgress}
              sx={{
                borderColor: "#d3d3d3",
                color: "#1E4DE3",
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  borderColor: "#1E4DE3",
                  boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                  backgroundColor: "#e8f0fe",
                },
                "&.Mui-disabled": {
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                },
              }}
            >
              Share
            </Button>

            <Button
              variant="outlined"
              startIcon={<MdOutlineFeedback style={{ color: "#1E4DE3" }} />}
              onClick={handleOpenFeedback}
              sx={{
                borderColor: "#d3d3d3",
                color: "#1E4DE3",
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  borderColor: "#1E4DE3",
                  boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                  backgroundColor: "#e8f0fe",
                },
              }}
            >
              Feedback
            </Button>

          </div>
        </div>
      </DialogTitle>

      <DialogContent style={{ padding: "16px" }}>
 
        <div
          style={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%", // 16:9 ratio
            backgroundColor: "#000",
            marginBottom: "16px",
          }}
        >
          {loadingVideo || loadingVersionInfo ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <CircularProgress 
                variant={downloadProgress > 0 ? "determinate" : "indeterminate"}
                value={downloadProgress}
                sx={{ color: "#1E4DE3" }} 
              />
              <p style={{ color: "#fff", margin: 0 }}>
                {downloadProgress > 0 ? `Loading video... ${downloadProgress}%` : "Loading video..."}
              </p>
            </div>
          ) : displayVideoSrc ? (
            <div 
              ref={videoContainerRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#000",
                overflow: "hidden"
              }}
            >
              <video
                ref={videoRef}
                src={displayVideoSrc}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={(e) => {
                  setDuration(e.target.duration);
                  if (videoRef.current) {
                    videoRef.current.volume = volume;
                    videoRef.current.muted = isMuted;
                  }
                }}
                onClick={handlePlayPause}
              >
                {showCaptions && captionsUrl && (
                  <track
                    kind="captions"
                    src={captionsUrl}
                    srcLang="en"
                    label="English"
                    default
                    onCueChange={handleCueChange}
                  />
                )}
              </video>

              {/* Custom Caption Overlay - Positioned higher as requested */}
              {showCaptions && activeCaptionText && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "80px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    maxWidth: "80%",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    textAlign: "center",
                    zIndex: 2,
                    pointerEvents: "none"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.4,
                      textShadow: "0px 1px 2px rgba(0,0,0,0.8)"
                    }}
                  >
                    {activeCaptionText}
                  </Typography>
                </Box>
              )}

              {/* Custom Video Controls Overlay */}
              <Box sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                padding: "20px 16px 12px",
                opacity: 0,
                transition: "opacity 0.3s",
                "&:hover": { opacity: 1 },
                // Show controls always if paused
                ...(!isPlaying && { opacity: 1 })
              }}>
                {/* Progress Bar */}
                <Box
                  sx={{
                    height: 4,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    borderRadius: 2,
                    mb: 1,
                    cursor: "pointer",
                    position: "relative"
                  }}
                  onClick={(e) => {
                    if (videoRef.current && duration) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const newTime = (clickX / rect.width) * duration;
                      videoRef.current.currentTime = newTime;
                    }
                  }}
                >
                  <Box sx={{
                    height: "100%",
                    width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                    backgroundColor: PRIMARY_BLUE,
                    borderRadius: 2
                  }} />
                </Box>

                {/* Controls Bar */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      onClick={handlePlayPause}
                      size="small"
                      sx={{
                        backgroundColor: PRIMARY_BLUE,
                        color: "#fff",
                        "&:hover": { backgroundColor: "#1e3bb3" },
                        p: 0.5
                      }}
                    >
                      {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                    
                    <Typography sx={{ color: "#fff", fontSize: 12, ml: 1 }}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                  </Box>

                  <Box 
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    <IconButton 
                      size="small" 
                      sx={{ color: "#fff" }}
                      onClick={() => setIsMuted(!isMuted)}
                    >
                      {isMuted || volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                    </IconButton>

                    {showVolumeSlider && (
                      <Box sx={{ width: 80, display: "flex", alignItems: "center", mr: 1 }}>
                        <Slider
                          size="small"
                          value={isMuted ? 0 : volume * 100}
                          onChange={(e, newValue) => {
                            const newVol = newValue / 100;
                            setVolume(newVol);
                            setIsMuted(newVol === 0);
                            if (videoRef.current) {
                              videoRef.current.volume = newVol;
                            }
                          }}
                          sx={{
                            color: "#fff",
                            "& .MuiSlider-thumb": {
                              width: 12,
                              height: 12,
                              "&:hover, &.Mui-focusVisible, &.Mui-active": {
                                boxShadow: "none",
                              },
                            },
                            "& .MuiSlider-track": {
                              border: "none",
                            },
                            "& .MuiSlider-rail": {
                              opacity: 0.3,
                            },
                          }}
                        />
                      </Box>
                    )}

                    <Tooltip title={showCaptions ? "Hide Captions" : "Show Captions"} arrow>
                      <IconButton
                        size="small"
                        sx={{ color: showCaptions ? PRIMARY_BLUE : "#fff" }}
                        onClick={() => setShowCaptions(!showCaptions)}
                        disabled={loadingCaptions}
                      >
                        {loadingCaptions ? (
                          <CircularProgress size={16} sx={{ color: PRIMARY_BLUE }} />
                        ) : (
                          showCaptions ? <ClosedCaptionIcon fontSize="small" /> : <ClosedCaptionDisabledIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>

                    <IconButton
                      size="small"
                      sx={{ color: "#fff" }}
                      onClick={handleFullscreen}
                    >
                      <Box component="span" sx={{ fontSize: 14 }}>⛶</Box>
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </div>
          ) : (
            <p
              style={{
                color: "#fff",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                margin: 0,
              }}
            >
              No video available.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Snackbar for alerts - displayed outside the dialog */}
    <Snackbar
      open={!!alertInfo}
      autoHideDuration={3000}
      onClose={handleCloseAlert}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert 
        onClose={handleCloseAlert} 
        severity={alertInfo?.severity || "info"}
        sx={{ width: '100%' }}
      >
        {alertInfo?.message}
      </Alert>
    </Snackbar>

    {/* Share Link Dialog */}
    <Dialog
      open={shareDialogOpen}
      onClose={() => setShareDialogOpen(false)}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            padding: "8px",
            minWidth: 400,
            backgroundColor: "#2d2d2d",
            color: "#fff",
          },
        },
      }}
    >
      <DialogTitle sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        padding: "12px 16px",
        color: "#fff"
      }}>
        <span style={{ fontSize: "16px" }}>Link to 'Pubvision Video' copied</span>
        <IconButton
          onClick={() => setShareDialogOpen(false)}
          sx={{ color: "#fff", padding: "4px" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "0 16px 16px 16px" }}>
        {sharingInProgress ? (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}>
            <CircularProgress size={24} sx={{ color: "#fff" }} />
            <span style={{ marginLeft: "12px", color: "#fff" }}>Loading share link...</span>
          </div>
        ) : (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#3d3d3d",
            borderRadius: "4px",
            padding: "8px 12px",
          }}>
            <input
              type="text"
              value={shareUrl}
              readOnly
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: "14px",
                outline: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
            <Button
              variant="contained"
              onClick={handleCopyShareUrl}
              startIcon={<ContentCopyIcon />}
              sx={{
                backgroundColor: "#4a4a4a",
                color: "#fff",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#5a5a5a",
                },
              }}
            >
              Copy
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Feedback Dialog */}
    <Dialog
      open={feedbackDialogOpen}
      onClose={() => setFeedbackDialogOpen(false)}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            padding: "16px",
            minWidth: 400,
            maxWidth: 450,
            maxHeight: "90vh",
          },
        },
      }}
    >
      <DialogTitle sx={{ 
        padding: "0 0 16px 0",
        fontWeight: 600,
        fontSize: "18px",
      }}>
        Help us improve our product for you
      </DialogTitle>
      <DialogContent sx={{ padding: 0, maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}>
        {loadingFeedbacks ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <CircularProgress size={24} sx={{ color: "#1E4DE3" }} />
          </Box>
        ) : (
          <>
            {/* Previous Feedbacks Section */}
            {previousFeedbacks && previousFeedbacks.length > 0 && (
              <Box sx={{ marginBottom: "24px" }}>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", marginBottom: "12px", color: "#333" }}>
                  Previous Reviews
                </Typography>
                <Box sx={{ maxHeight: "300px", overflowY: "auto", paddingRight: "8px" }}>
                  {previousFeedbacks.map((feedback) => {
                    const ratingOption = ratingOptions.find(opt => opt.value === feedback.stars);
                    const feedbackDate = new Date(feedback.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    });
                    
                    return (
                      <Box
                        key={feedback.feedback_id}
                        sx={{
                          padding: "12px",
                          marginBottom: "12px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "24px", marginRight: "8px" }}>
                            {ratingOption ? ratingOption.emoji : "😐"}
                          </span>
                          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#666" }}>
                            {ratingOption ? ratingOption.label : `${feedback.stars} stars`}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", color: "#999", marginLeft: "auto" }}>
                            {feedbackDate}
                          </Typography>
                        </Box>
                        {feedback.comments && (
                          <Typography sx={{ fontSize: "13px", color: "#555", lineHeight: 1.5 }}>
                            {feedback.comments}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", marginTop: "20px", marginBottom: "12px", color: "#333" }}>
                  Add New Review
                </Typography>
              </Box>
            )}

            {/* New Feedback Form */}
            <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
              {previousFeedbacks && previousFeedbacks.length > 0 
                ? "How would you like to rate this version?"
                : "How would you like to describe your experience with the app today?"}
            </p>
            
            {/* Rating Emojis */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}>
              {ratingOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => setFeedbackRating(option.value)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    backgroundColor: feedbackRating === option.value ? "#e8f0fe" : "transparent",
                    border: feedbackRating === option.value ? "2px solid #1E4DE3" : "2px solid transparent",
                    transition: "all 0.2s",
                  }}
                >
                  <span style={{ fontSize: "32px" }}>{option.emoji}</span>
                  <span style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>{option.label}</span>
                </div>
              ))}
            </div>

            {/* Comments TextField */}
            <TextField
              multiline
              rows={4}
              placeholder="Tell us more about your experience (optional)"
              value={feedbackComments}
              onChange={(e) => setFeedbackComments(e.target.value.slice(0, 500))}
              fullWidth
              sx={{
                marginBottom: "8px",
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                },
              }}
            />
            <p style={{ margin: "0 0 16px 0", color: "#999", fontSize: "12px" }}>
              {500 - feedbackComments.length}/500 characters left
            </p>

            {/* Consent Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={feedbackConsent}
                  onChange={(e) => setFeedbackConsent(e.target.checked)}
                  sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                />
              }
              label={
                <span style={{ fontSize: "13px", color: "#666" }}>
                  I consent to PubVision AI contacting me based on my feedback
                </span>
              }
            />

            {/* Buttons */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "20px",
            }}>
              <Button
                variant="outlined"
                onClick={() => setFeedbackDialogOpen(false)}
                sx={{
                  textTransform: "none",
                  borderColor: "#ccc",
                  color: "#333",
                  "&:hover": {
                    borderColor: "#999",
                  },
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || feedbackRating === 0}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#1E4DE3",
                  "&:hover": {
                    backgroundColor: "#1a3fb8",
                  },
                }}
              >
                {submittingFeedback ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit feedback"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};
AbstractVideoViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  videoSrc: PropTypes.string,
  videoProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStartRegenerate: PropTypes.func,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
};

export default AbstractVideoViewDialog;

